import { curveLinearClosed } from '@visx/curve';
import { Group } from '@visx/group';
import { scaleLinear } from '@visx/scale';
import { Circle, Line, LineRadial } from '@visx/shape';
import { Text } from '@visx/text';
import React from 'react';
import { RadarAxis, RadarSeries, radarPoint } from './attributeRadar.utils';

/**
 * The attribute radar.
 *
 * Answers the one question the leaderboard cannot: what SHAPE does a brand have across the
 * attributes AI talks about — broad and even, or spiked on two and absent elsewhere. The table is
 * one click away and remains the place to read exact numbers; this is for the shape.
 *
 * Built from visx primitives, like every other chart here: `LineRadial` closed with
 * `curveLinearClosed` draws the rings and the series, `scaleLinear` maps a score to a radius, and
 * `Line` / `Circle` / `Text` place the furniture. visx ships no radar, but composing one from its
 * radial primitives keeps the angle-to-cartesian conversion inside the library the trends and
 * breakdown charts already use, instead of a second hand-rolled implementation of it.
 *
 * Every selection rule that keeps the shape honest — ranking by share, omitting unmeasured axes
 * rather than zeroing them, capping the series — lives in `attributeRadar.utils` and is tested
 * there. This file only draws what it is handed.
 */

/**
 * Drawn radius at a score of 100, in viewBox units.
 *
 * Sized against the label gutter rather than chosen freely: the component's rendered height is
 * fixed, so every unit the gutter does not need is a unit the plot can have. The old pairing left
 * a wide empty band above and below the rings.
 */
const RADIUS = 154;
/** Rings behind the polygons, as percentages. */
const RINGS = [25, 50, 75, 100];
/**
 * Room around the ring for the axis labels.
 *
 * Must cover the label's radial offset plus the longest label's own width, or a label at three
 * o'clock clips the viewBox edge.
 */
const LABEL_MARGIN = 64;
/**
 * How far past the rim a label sits, as a multiple of the rim radius.
 *
 * Applied by scaling the rim point outward, NOT by asking `radarPoint` for a percentage above 100:
 * that helper clamps its score to 0–100, so the previous 128 was silently clamped back to the rim
 * and every label ended up touching the outer ring.
 *
 * Radial rather than a fixed vertical nudge, because a constant y-shift clears the ring at top and
 * bottom while leaving the left and right labels sitting on it — those are the positions where the
 * ring runs vertically. Scaling along each label's own radius clears all four evenly.
 */
const LABEL_OFFSET_FACTOR = 1.1;

const VIEW = RADIUS + LABEL_MARGIN;

/**
 * The component's drawn height.
 *
 * Raised with the fill ratio rather than instead of it: a bigger box alone would have scaled the
 * same wasted margin up with everything else. `RADIUS / VIEW` is the share of the box the plot
 * actually occupies, and it is the number that decides whether the chart looks cramped.
 */
const MAX_HEIGHT_PX = 504;

/**
 * One corner of a series' polygon: the axis it sits on, and the score that was measured there.
 *
 * Only ever built for a measured score, which is what keeps the outline off the centre. Carrying
 * the axis index rather than the axis itself is what lets a polygon skip an axis while every corner
 * it does draw stays at that axis's own angle.
 */
interface RadarVertex {
  axisIndex: number;
  score: number;
}

export function AttributeRadarChart({ axes, series }: { axes: readonly RadarAxis[]; series: readonly RadarSeries[] }) {
  // Score to radius. Shared by the rings and every series, so a ring at 50 and a vertex at 50 sit
  // the same distance from the centre by construction rather than by coincidence.
  const radiusScale = scaleLinear<number>({ domain: [0, 100], range: [0, RADIUS] });

  /**
   * An axis's angle, in the convention d3's radial line uses: zero at twelve o'clock, running
   * clockwise. The first (highest-share) axis is therefore the one the eye lands on, and it is the
   * same convention `radarPoint` places the furniture with.
   */
  const angleOf = (index: number) => (index / Math.max(1, axes.length)) * Math.PI * 2;
  const indices = axes.map((_, index) => index);

  /**
   * The corners one series actually has.
   *
   * An axis this series was not measured on contributes NO corner — it is skipped, never emitted at
   * a radius of zero. A zero is a point on the centre, and it reads as "this brand scored nothing
   * here" rather than "this brand was not measured here": a value the product never took, invented
   * by the renderer. The vertex dots below already omitted it; the outline did not, so a rival
   * missing a measurement had its polygon dragged to the middle of the chart.
   */
  const verticesOf = (seriesId: string): RadarVertex[] =>
    axes.flatMap((axis, axisIndex) => {
      const score = axis.scores.get(seriesId);
      return score === undefined || !Number.isFinite(score) ? [] : [{ axisIndex, score }];
    });

  return (
    <div className="w-full">
      <svg viewBox={`${-VIEW} ${-VIEW} ${VIEW * 2} ${VIEW * 2}`} className="w-full" style={{ maxHeight: `${MAX_HEIGHT_PX}px` }} role="img">
        <Group>
          {/* Scale furniture first, so it sits behind the data it measures. */}
          {RINGS.map((ring) => (
            <LineRadial
              key={`ring:${ring}`}
              data={indices}
              angle={(index) => angleOf(index)}
              radius={() => radiusScale(ring)}
              curve={curveLinearClosed}
              fill="none"
              stroke="#E2E8F0"
              strokeWidth={1}
            />
          ))}

          {axes.map((axis, index) => {
            const outer = radarPoint(index, axes.length, 100, RADIUS);
            const label = { x: outer.x * LABEL_OFFSET_FACTOR, y: outer.y * LABEL_OFFSET_FACTOR };
            // Labels flip their anchor around the vertical so they never overlap the polygon.
            const anchor = Math.abs(label.x) < 1 ? 'middle' : label.x > 0 ? 'start' : 'end';

            return (
              <React.Fragment key={`axis:${axis.termKey}`}>
                <Line from={{ x: 0, y: 0 }} to={{ x: outer.x, y: outer.y }} stroke="#E2E8F0" strokeWidth={1} />
                <Text x={label.x} y={label.y} textAnchor={anchor} verticalAnchor="middle" fontSize={11} fill="#64748B">
                  {axis.label}
                </Text>
              </React.Fragment>
            );
          })}

          {series.map((entry) => (
            <LineRadial<RadarVertex>
              key={`series:${entry.id}`}
              data={verticesOf(entry.id)}
              angle={(vertex) => angleOf(vertex.axisIndex)}
              radius={(vertex) => radiusScale(vertex.score)}
              curve={curveLinearClosed}
              fill={entry.color}
              // Own sits slightly stronger than the rivals, matching the You-first convention
              // everywhere else, without changing its hue.
              fillOpacity={entry.isOwn ? 0.22 : 0.12}
              stroke={entry.color}
              strokeWidth={entry.isOwn ? 2.5 : 1.75}
            />
          ))}

          {/* Vertices last, so a point is never hidden under another series' fill. */}
          {series.map((entry) =>
            // The same corners the outline was drawn through, so a dot and the polygon can never
            // disagree about which axes this series was measured on.
            verticesOf(entry.id).map((vertex) => {
              const axis = axes[vertex.axisIndex];
              const point = radarPoint(vertex.axisIndex, axes.length, vertex.score, RADIUS);
              const describe = `${entry.label} — ${axis.label}: ${Math.round(vertex.score)}`;
              return (
                <Circle
                  key={`point:${entry.id}:${axis.termKey}`}
                  cx={point.x}
                  cy={point.y}
                  r={entry.isOwn ? 3.5 : 2.5}
                  fill={entry.color}
                  aria-label={describe}
                >
                  <title>{describe}</title>
                </Circle>
              );
            })
          )}
        </Group>
      </svg>

      <div className="d-flex align-items-center justify-content-center gap-3 flex-wrap mt-2">
        {series.map((entry) => (
          <span key={`legend:${entry.id}`} className="d-flex align-items-center gap-1 text-[11px] text-slate-600">
            <span className="inline-block rounded-sm" style={{ width: 10, height: 10, backgroundColor: entry.color }} />
            {entry.label}
          </span>
        ))}
      </div>
    </div>
  );
}
